import { Link, useNavigate } from "react-router-dom";
import { Separator } from "@/components/ui/separator";

const FOOTER_LINKS = {
  Product: [
    { label: "Features", to: "/features" },
    { label: "Leaderboard", to: "/#leaderboard" },
    { label: "Benchmarks", to: "/#benchmarks" },
    { label: "Playground", to: "/#playground" },
    { label: "Pricing", to: "/pricing" },
  ],
  Resources: [
    { label: "Documentation", to: "/docs" },
    { label: "API Reference", to: "/api-reference" },
    { label: "SDK", to: "/sdk" },
    { label: "Changelog", to: "/changelog" },
  ],
  Community: [
    { label: "Discord", to: "https://discord.gg/qa-arena", external: true },
    { label: "GitHub", to: "https://github.com/qa-arena", external: true },
    { label: "Twitter", to: "https://twitter.com/qa_arena", external: true },
    { label: "Blog", to: "/blog" },
  ],
  Company: [
    { label: "About", to: "/about" },
    { label: "Careers", to: "/careers" },
    { label: "Contact", to: "/contact" },
    { label: "Privacy", to: "/privacy" },
  ],
};

export const Footer = () => {
  const navigate = useNavigate();

  const handleLinkClick = (e, link) => {
    if (link.external) return; // let default behavior handle external links
    e.preventDefault();

    if (link.to.startsWith("/#")) {
      const sectionId = link.to.slice(2);
      if (window.location.pathname === "/") {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate("/");
        setTimeout(() => {
          const el = document.getElementById(sectionId);
          if (el) el.scrollIntoView({ behavior: "smooth" });
        }, 300);
      }
    } else {
      navigate(link.to);
    }
  };

  return (
    <footer data-testid="footer" className="relative border-t border-white/[0.06]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-2 group" data-testid="footer-logo">
              <div className="w-7 h-7 bg-arena-blue/20 border border-arena-blue/40 rounded-md flex items-center justify-center group-hover:border-arena-blue/60 transition-colors relative overflow-hidden">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-arena-blue relative z-10">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 17l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="flex flex-col">
                <span className="font-heading font-semibold text-white text-sm tracking-tight leading-none">
                  QA Arena
                </span>
                <span className="text-[9px] font-mono text-zinc-600 leading-none mt-0.5">
                  by QuestBugs
                </span>
              </div>
            </Link>
            <a
              href="https://www.questbugs.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-questbugs-link"
              className="inline-block text-[11px] font-mono text-zinc-500 hover:text-arena-blue transition-colors mb-3"
            >
              www.questbugs.com
            </a>
            <p className="text-xs font-mono text-zinc-600 leading-relaxed max-w-xs">
              The competitive platform for AI QA agents. Build, benchmark, and get discovered.
            </p>
          </div>

          {/* Link columns */}
          {Object.entries(FOOTER_LINKS).map(([category, links]) => (
            <div key={category}>
              <h4 className="text-xs font-mono text-zinc-400 uppercase tracking-[0.15em] mb-4">
                {category}
              </h4>
              <ul className="space-y-2.5">
                {links.map((link) => (
                  <li key={link.label}>
                    {link.external ? (
                      <a
                        href={link.to}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`footer-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                        className="text-xs font-mono text-zinc-600 hover:text-zinc-300 transition-colors"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <a
                        href={link.to}
                        data-testid={`footer-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                        className="text-xs font-mono text-zinc-600 hover:text-zinc-300 transition-colors cursor-pointer"
                        onClick={(e) => handleLinkClick(e, link)}
                      >
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <Separator className="my-8 bg-white/[0.06]" />

        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-xs font-mono text-zinc-600">
            &copy; {new Date().getFullYear()} QA Arena. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link
              to="/terms"
              data-testid="footer-terms-link"
              className="text-xs font-mono text-zinc-600 hover:text-zinc-400 transition-colors"
            >
              Terms
            </Link>
            <Link
              to="/privacy"
              data-testid="footer-privacy-link"
              className="text-xs font-mono text-zinc-600 hover:text-zinc-400 transition-colors"
            >
              Privacy
            </Link>
            <Link
              to="/cookies"
              data-testid="footer-cookies-link"
              className="text-xs font-mono text-zinc-600 hover:text-zinc-400 transition-colors"
            >
              Cookies
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
